import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BillingAnalyticsTable = () => {
  const [selectedTab, setSelectedTab] = useState('payments');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const paymentData = [
    {
      id: 1,
      date: '2024-07-25',
      customer: 'TechCorp Solutions',
      amount: 15000,
      status: 'successful',
      method: 'Credit Card',
      plan: 'Enterprise',
      retryCount: 0
    },
    {
      id: 2,
      date: '2024-07-25',
      customer: 'DataFlow Inc',
      amount: 8500,
      status: 'failed',
      method: 'Bank Transfer',
      plan: 'Professional',
      retryCount: 2,
      nextRetry: '2024-07-27'
    },
    {
      id: 3,
      date: '2024-07-24',
      customer: 'CloudSync Ltd',
      amount: 3200,
      status: 'successful',
      method: 'Credit Card',
      plan: 'Starter',
      retryCount: 0
    },
    {
      id: 4,
      date: '2024-07-24',
      customer: 'InnovateLab',
      amount: 12000,
      status: 'pending',
      method: 'ACH',
      plan: 'Enterprise',
      retryCount: 1,
      nextRetry: '2024-07-26'
    },
    {
      id: 5,
      date: '2024-07-23',
      customer: 'StartupHub',
      amount: 2500,
      status: 'failed',
      method: 'Credit Card',
      plan: 'Starter',
      retryCount: 3,
      nextRetry: 'Manual Review'
    }
  ];

  const recoveryData = [
    {
      id: 1,
      customer: 'DataFlow Inc',
      originalAmount: 8500,
      recoveredAmount: 0,
      attempts: 2,
      lastAttempt: '2024-07-25',
      nextAction: 'Email Reminder',
      probability: 75
    },
    {
      id: 2,
      customer: 'StartupHub',
      originalAmount: 2500,
      recoveredAmount: 2500,
      attempts: 1,
      lastAttempt: '2024-07-22',
      nextAction: 'Completed',
      probability: 100
    },
    {
      id: 3,
      customer: 'MegaCorp',
      originalAmount: 25000,
      recoveredAmount: 0,
      attempts: 1,
      lastAttempt: '2024-07-20',
      nextAction: 'Phone Call',
      probability: 85
    }
  ];

  const tabs = [
    { id: 'payments', label: 'Payment Metrics', icon: 'CreditCard' },
    { id: 'recovery', label: 'Recovery Status', icon: 'RefreshCw' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'successful': return 'text-success bg-success/10';
      case 'failed': return 'text-error bg-error/10';
      case 'pending': return 'text-warning bg-warning/10';
      default: return 'text-text-secondary bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'successful': return 'CheckCircle';
      case 'failed': return 'XCircle';
      case 'pending': return 'Clock';
      default: return 'Circle';
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    const dataToSort = selectedTab === 'payments' ? paymentData : recoveryData;
    if (!sortConfig.key) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [selectedTab, sortConfig, paymentData, recoveryData]);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-text-secondary" />;
    }
    return (
      <Icon 
        name={sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg dashboard-fade-in">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-1">Billing Analytics</h3>
            <p className="text-text-secondary text-sm">Payment processing and recovery metrics</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex bg-muted rounded-lg p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-150 ${
                    selectedTab === tab.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
            
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
              Export
            </Button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {selectedTab === 'payments' ? (
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">
                  <button 
                    onClick={() => handleSort('date')}
                    className="flex items-center space-x-2 hover:text-text-primary transition-colors"
                  >
                    <span>Date</span>
                    <SortIcon column="date" />
                  </button>
                </th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">
                  <button 
                    onClick={() => handleSort('customer')}
                    className="flex items-center space-x-2 hover:text-text-primary transition-colors"
                  >
                    <span>Customer</span>
                    <SortIcon column="customer" />
                  </button>
                </th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">
                  <button 
                    onClick={() => handleSort('amount')}
                    className="flex items-center space-x-2 hover:text-text-primary transition-colors"
                  >
                    <span>Amount</span>
                    <SortIcon column="amount" />
                  </button>
                </th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">Status</th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">Method</th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">Plan</th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">Retry Info</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((payment) => (
                <tr key={payment.id} className="border-b border-border hover:bg-muted/20 transition-colors">
                  <td className="p-4">
                    <span className="text-text-primary text-sm">
                      {new Date(payment.date).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-text-primary font-medium text-sm">{payment.customer}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-text-primary font-medium text-sm">
                      ${payment.amount.toLocaleString()}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                      <Icon name={getStatusIcon(payment.status)} size={12} />
                      <span className="capitalize">{payment.status}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-text-secondary text-sm">{payment.method}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-text-secondary text-sm">{payment.plan}</span>
                  </td>
                  <td className="p-4">
                    {payment.retryCount > 0 ? (
                      <div className="text-sm">
                        <p className="text-text-primary">Retries: {payment.retryCount}</p>
                        {payment.nextRetry && (
                          <p className="text-text-secondary text-xs">Next: {payment.nextRetry}</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-text-secondary text-sm">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">
                  <button 
                    onClick={() => handleSort('customer')}
                    className="flex items-center space-x-2 hover:text-text-primary transition-colors"
                  >
                    <span>Customer</span>
                    <SortIcon column="customer" />
                  </button>
                </th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">
                  <button 
                    onClick={() => handleSort('originalAmount')}
                    className="flex items-center space-x-2 hover:text-text-primary transition-colors"
                  >
                    <span>Original Amount</span>
                    <SortIcon column="originalAmount" />
                  </button>
                </th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">Recovered</th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">Attempts</th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">Last Attempt</th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">Next Action</th>
                <th className="text-left p-4 text-text-secondary text-sm font-medium">Probability</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((recovery) => (
                <tr key={recovery.id} className="border-b border-border hover:bg-muted/20 transition-colors">
                  <td className="p-4">
                    <span className="text-text-primary font-medium text-sm">{recovery.customer}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-text-primary font-medium text-sm">
                      ${recovery.originalAmount.toLocaleString()}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`font-medium text-sm ${recovery.recoveredAmount > 0 ? 'text-success' : 'text-text-secondary'}`}>
                      ${recovery.recoveredAmount.toLocaleString()}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-text-secondary text-sm">{recovery.attempts}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-text-secondary text-sm">
                      {new Date(recovery.lastAttempt).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-text-primary text-sm">{recovery.nextAction}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-border rounded-full h-2">
                        <div 
                          className="h-2 bg-success rounded-full transition-all duration-300"
                          style={{ width: `${recovery.probability}%` }}
                        />
                      </div>
                      <span className="text-text-secondary text-xs">{recovery.probability}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="p-6 border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-text-secondary text-sm mb-1">Success Rate</p>
            <p className="text-text-primary font-bold text-lg">94.2%</p>
          </div>
          <div className="text-center">
            <p className="text-text-secondary text-sm mb-1">Failed Payments</p>
            <p className="text-error font-bold text-lg">$11,000</p>
          </div>
          <div className="text-center">
            <p className="text-text-secondary text-sm mb-1">Recovery Rate</p>
            <p className="text-success font-bold text-lg">68.5%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingAnalyticsTable;